import { takeLatest, put, all, call } from "redux-saga/effects";
import { ReduxActionTypes } from "constants/ReduxActionConstants";
import {
  fetchLicenseDetailsSuccess,
  fetchLicenseDetailsFailed,
  startTrialFailed,
  updateFlowType,
} from "actions/LicenseActions";
import LicenseApi from "api/LicenseApi";
import { ApiResponse } from "types/ApiTypes";
import {
  BillingModel,
  FlowType,
  LicenseResponseType,
} from "types/LicenseTypes";
import { authenticateUserSuccess } from "actions/UserActions";
import { toast } from "@appsmith/ads";
import log from "loglevel";
import { createMessage, LICESNE_FETCH_ERROR } from "constants/Messages";
import { isValidResponse } from "./utilitySaga";
import { fetchAccountDetails } from "actions/AccountActions";
import { fetchLicenseDetails } from "actions/LicenseActions";
import { PLANS } from "constants/RouteContants";
import { fetchUsage } from "actions/UsageActions";
import { USAGE_STATES } from "constants/AccountTypeConstants";
import { ReduxAction } from "types/ReduxTypes";

export function showErrorToaster() {
  toast.show(createMessage(LICESNE_FETCH_ERROR), {
    kind: "error",
  });
}

export function* onFetchLicenseDetails() {
  try {
    const response: ApiResponse<LicenseResponseType> = yield call(
      LicenseApi.fetchCurrent,
    );

    const isValid: boolean = yield call(isValidResponse, response);

    if (isValid) {
      yield put(fetchLicenseDetailsSuccess(response.data));
      if (
        USAGE_STATES.includes(response.data.licenseAccountStatus) &&
        response.data.billingModel !== BillingModel.SEAT_BASED
      )
        yield put(fetchUsage());
    } else {
      throw new Error(createMessage(LICESNE_FETCH_ERROR));
    }
  } catch (error: any) {
    log.error(error);
    yield put(fetchLicenseDetailsFailed(error.message));
    yield call(showErrorToaster);
  }

  yield put(authenticateUserSuccess());
}

export function* onStartTrial() {
  try {
    yield call(LicenseApi.startTrial);
    yield put(fetchAccountDetails());
    yield put(fetchLicenseDetails());
    if (window.location.pathname.endsWith(PLANS)) {
      window.location.replace(`${window.location.origin}/license`);
    }
  } catch (error) {
    log.error(error);
    yield put(startTrialFailed());
    yield call(showErrorToaster);
  }
}

export function* onFetchLicenseDetailsSuccess(
  action: ReduxAction<LicenseResponseType>,
) {
  const { billingModel, licenseStatus, licenseType } = action.payload;
  if (
    (billingModel === BillingModel.USAGE_BASED && licenseStatus === "ACTIVE") ||
    !billingModel
  ) {
    yield put(updateFlowType(FlowType.USAGE_BASED));
  } else if (
    billingModel === BillingModel.SEAT_BASED &&
    licenseType === "PAID" &&
    (licenseStatus === "ACTIVE" || licenseStatus === "IN_GRACE_PERIOD")
  ) {
    yield put(updateFlowType(FlowType.SEAT_BASED_UPDATE));
  } else {
    yield put(updateFlowType(FlowType.SEAT_BASED));
  }
}

export function* LicenseSaga() {
  yield all([
    takeLatest(ReduxActionTypes.FETCH_LICENSE, onFetchLicenseDetails),
    takeLatest(ReduxActionTypes.START_TRIAL, onStartTrial),
    takeLatest(
      ReduxActionTypes.FETCH_LICENSE_SUCCESS,
      onFetchLicenseDetailsSuccess,
    ),
  ]);
}
